import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CellValueChangedEvent, ILargeTextEditorParams } from 'ag-grid-community';
import DialogAddSUMSCriteria from '@/components/DialogAddSUMSCriteria/DialogAddSUMSCriteria';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import { CELL_EDITOR_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import { validateSUMSCriteria } from '@/helpers/administrationUtils';
import { ASC_ORDER, formYESNO } from '@/helpers/constants';
import { langFormFormatter } from '@/helpers/tables-utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import { SUMSCriteriaINT } from '@/types';
import styles from './SUMSCriteria.module.scss';

const COLUMN_NAMES = {
  EFA_NUMBER: 'efaNumber',
  DESCRIPTION: 'description',
  SUMS_RELEVANT: 'sumsRelevant',
  DEFINITION: 'definition',
  COMMENT: 'comment',
};

const SUMSCriteria = () => {
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [sumsCriteria, setSumsCriteria] = useState<SUMSCriteriaINT[]>([]);
  const [popupOpen, setPopupOpen] = useState('');
  const [selection, setSelection] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToast } = useToastContext();
  const { setLoading: setGlobalLoading } = useGlobalSpinnerContext();

  const currentSelection = useMemo(() => selection?.map((efaNumber) => sumsCriteria
    .find((ms) => ms.efaNumber === efaNumber)), [selection, sumsCriteria]);
  const columns = [
    {
      name: COLUMN_NAMES.EFA_NUMBER,
      title: t('administration.sumsCriteria.efaNumber'),
      flex: 1.3,
      wrapText: true,
    },
    {
      name: COLUMN_NAMES.DESCRIPTION,
      title: t('administration.sumsCriteria.description'),
      flex: 3,
      wrapText: true,
      editable: true,
      cellEditor: CELL_EDITOR_TYPES.LARGE_TEXT,
      cellEditorParams: { maxLength: 100, rows: 2 } as ILargeTextEditorParams,
    },
    {
      name: COLUMN_NAMES.SUMS_RELEVANT,
      title: t('administration.sumsCriteria.sumsRelevant'),
      flex: 1.5,
      wrapText: true,
      editable: true,
      filterType: FILTER_TYPES.SET,
      cellEditor: CELL_EDITOR_TYPES.SELECT,
      cellEditorParams: {
        values: ['YES', 'NO'],
        formatValue: (value: string) => langFormFormatter(value, formYESNO, lang),
      },
      valueFormatter: ({ value }: { value: string }) => langFormFormatter(value, formYESNO, lang),
    },
    {
      name: COLUMN_NAMES.DEFINITION,
      title: t('administration.sumsCriteria.definition'),
      flex: 3.5,
      wrapText: true,
      editable: true,
      cellEditor: CELL_EDITOR_TYPES.LARGE_TEXT,
      cellEditorParams: { maxLength: 250, rows: 3 } as ILargeTextEditorParams,

    },
    {
      name: COLUMN_NAMES.COMMENT,
      title: t('administration.sumsCriteria.comment'),
      flex: 3,
      wrapText: true,
      editable: true,
      cellEditor: CELL_EDITOR_TYPES.LARGE_TEXT,
      cellEditorParams: { maxLength: 250, rows: 3 } as ILargeTextEditorParams,
    },
  ];

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('menu.sumsCriteria'));
  }, [t, lang]);

  const initTable = () => {
    setLoading(true);
    administrationServices.getSUMSCriteria()
      .then((res) => setSumsCriteria(res.sumsCriteria))
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setLoading(false);
        setPopupOpen('');
      });
  };

  useEffect(() => {
    initTable();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const editSUMSCriteria = (event: CellValueChangedEvent) => {
    const { newValue: newCellValue, oldValue: oldCellValue, data: rowData, colDef: { field }, api } = event;
    const newSUMSCriteria = sumsCriteria.map((pg) => (pg.efaNumber === rowData.efaNumber ? { ...rowData, [field!]: newCellValue } : pg));
    const editedCriteria = newSUMSCriteria.find((pg) => pg.efaNumber === rowData.efaNumber);

    if (validateSUMSCriteria(editedCriteria, sumsCriteria, addToast, t, true)) {
      setSumsCriteria(newSUMSCriteria);
      setGlobalLoading(true);
      administrationServices.editCriteria(editedCriteria.efaNumber, editedCriteria)
        .catch((error) => {
          setSumsCriteria(sumsCriteria);
          api.applyTransaction({ update: [{ ...rowData, [field!]: oldCellValue }] });
          addToast(error.message, 'ERROR');
        })
        .finally(() => setGlobalLoading(false));
    }
  };

  const handleDeleteCriterias = () => {
    setLoading(true);
    const criteriasSelected = currentSelection.map((cr) => cr?.efaNumber);

    administrationServices.deleteSUMSCriteria(criteriasSelected)
      .then(() => { initTable(); })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setPopupOpen('');
        setSelection([]);
        setLoading(false);
      });
  };

  return (
    <>
      <div className={styles.Plants}>
        <AccordionPanel title={`${t('menu.sumsCriteria')} ${sumsCriteria ? `(${sumsCriteria.length})` : ''}`}>
          {loading && (<Spinner isVisible isMask />)}
          {!loading && (
            <>
              <div className={styles.ButtonsTable}>
                <div className={styles.ButtonsContentTableRight}>
                  <IcoButtonSimple
                    disabled={Boolean(selection.length === 0)}
                    id="btn-delete-SUMS-criteria"
                    icon="icon-muelleimer-loeschen"
                    tooltipText={t('software.delete')}
                    handleClick={() => setPopupOpen('DELETION')}
                  />
                  <SimpleButton
                    id="btn-add-SUMS-criteria"
                    color="primary"
                    buttonText={t('administration.sumsCriteria.addCriteria')}
                    handleClick={() => setPopupOpen('CREATION')}
                  />
                </div>
              </div>
              <div className="w-100">
                <TableListEditableNew
                  isFiltering
                  hasCheckbox
                  list={sumsCriteria}
                  selection={selection}
                  columnId={COLUMN_NAMES.EFA_NUMBER}
                  onSelectionChange={(rows) => setSelection(rows as string[])}
                  onCellValueChanged={editSUMSCriteria}
                  columns={columns}
                  columnSorting={[{ columnName: COLUMN_NAMES.EFA_NUMBER, direction: ASC_ORDER }]}
                />
              </div>
            </>
          )}
        </AccordionPanel>
      </div>
      {popupOpen === 'DELETION' && (
        <ConfirmDialog
          reject={() => setPopupOpen('')}
          confirm={handleDeleteCriterias}
          title={t('dialog.deleteModelSeries.title')}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.remove')}
        >
          <p className={styles.RemoveCriteriaMessage}>
            {`${t(`dialog.deleteSUMSCriteria.${currentSelection.length === 1 ? 'messageSingular' : 'message'}`)}: `}
            <b>{currentSelection.map((ms) => ms?.efaNumber).join(', ')}</b>
          </p>
        </ConfirmDialog>
      )}
      {popupOpen === 'CREATION' && (
        <DialogAddSUMSCriteria
          title={t('administration.sumsCriteria.addCriteria')}
          close={() => setPopupOpen('')}
          reloadSUMSCriteria={initTable}
          allCriterias={sumsCriteria}
        />
      )}
    </>
  );
};

export default SUMSCriteria;
